import React, {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Search } from "lucide-react";
import SearchInput from "./SearchInput";
import styles from "./StatusPill.module.scss";

/**
 * StatusPill — design-system primitive (DESIGN.md §2 status sözleşmesi).
 *
 * 3 mod:
 *   1. Salt görüntü:           <StatusPill value="To Do" colorMap={MAP} />
 *   2. Tıklanabilir dropdown:   <StatusPill value="To Do" options={[...]} onChange={fn} />
 *   3. Custom render:           options=[{ value, label, colors:{bg,fg,border} }]
 *
 * Renkler:
 *   - colorMap: { [statusValue]: { bg, fg, border } }    veya
 *   - options[i].colors: { bg, fg, border }              veya
 *   - hiçbiri verilmezse: nötr (--ds-surface/text/border)
 *
 * Renk inline CSS variable olarak set edilir (--pill-bg/fg/border).
 * Tüm yapısal CSS token-driven; dark-mode otomatik geçer.
 */

const DEFAULT_PALETTE = { bg: "", fg: "", border: "" };

const resolveColors = ({ value, options, colorMap }) => {
  if (options) {
    const opt = options.find((o) => (o.value ?? o) === value);
    if (opt?.colors) return opt.colors;
  }
  if (colorMap?.[value]) return colorMap[value];
  return DEFAULT_PALETTE;
};

const normalizeOptions = (options) =>
  (options || []).map((o) =>
    typeof o === "string" ? { value: o, label: o } : { label: o.value, ...o }
  );

const useClickOutside = (refs, onOutside, enabled) => {
  useEffect(() => {
    if (!enabled) return undefined;
    const handler = (e) => {
      const inside = refs.some((r) => r?.current && r.current.contains(e.target));
      if (!inside) onOutside?.(e);
    };
    document.addEventListener("mousedown", handler, true);
    document.addEventListener("touchstart", handler, true);
    return () => {
      document.removeEventListener("mousedown", handler, true);
      document.removeEventListener("touchstart", handler, true);
    };
  }, [refs, onOutside, enabled]);
};

const PillButton = forwardRef(function PillButton(
  { value, label, palette, size, interactive, dot, onClick, ariaLabel, ariaExpanded },
  ref
) {
  const styleVars = {
    "--pill-bg": palette.bg || undefined,
    "--pill-fg": palette.fg || undefined,
    "--pill-border": palette.border || undefined,
  };
  const sizeClass =
    size === "md" ? styles.md : size === "xs" ? styles.xs : styles.sm;

  const className = [
    styles.pill,
    sizeClass,
    interactive ? styles.interactive : "",
  ].join(" ");

  const inner = (
    <>
      {dot && <span className={styles.dot} aria-hidden="true" />}
      <span className={styles.label}>{label ?? value}</span>
      {interactive && (
        <span className={styles.chevron} aria-hidden="true">
          <ChevronDown size={16} />
        </span>
      )}
    </>
  );

  if (!interactive) {
    return (
      <span ref={ref} className={className} style={styleVars}>
        {inner}
      </span>
    );
  }

  return (
    <button
      ref={ref}
      type="button"
      className={className}
      style={styleVars}
      onClick={onClick}
      aria-haspopup="listbox"
      aria-expanded={ariaExpanded}
      aria-label={ariaLabel}
    >
      {inner}
    </button>
  );
});

const PillMenu = ({
  triggerRef,
  options,
  value,
  onPick,
  onClose,
  enableSearch,
  colorMap,
}) => {
  const menuRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const [q, setQ] = useState("");

  useLayoutEffect(() => {
    const t = triggerRef.current;
    if (!t) return;
    const rect = t.getBoundingClientRect();
    const menuW = 200;
    const margin = 8;
    let left = rect.left;
    if (left + menuW > window.innerWidth - margin) {
      left = Math.max(margin, window.innerWidth - menuW - margin);
    }
    setCoords({ top: rect.bottom + 6, left });
  }, [triggerRef]);

  useClickOutside([triggerRef, menuRef], onClose, true);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return options;
    return options.filter((o) => String(o.label).toLowerCase().includes(s));
  }, [q, options]);

  return createPortal(
    <div
      ref={menuRef}
      className={styles.menu}
      role="listbox"
      style={{ "--menu-top": `${coords.top}px`, "--menu-left": `${coords.left}px` }}
    >
      {enableSearch && (
        <div className={styles.menuSearch}>
          <SearchInput
            value={q}
            onChange={setQ}
            placeholder="Ara…"
            size="sm"
            inputProps={{ autoFocus: true }}
          />
        </div>
      )}
      <div className={styles.menuList}>
        {filtered.length === 0 ? (
          <div className={styles.menuEmpty}>Eşleşme yok</div>
        ) : (
          filtered.map((opt) => {
            const colors =
              opt.colors || colorMap?.[opt.value] || DEFAULT_PALETTE;
            const active = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                role="option"
                aria-selected={active}
                className={`${styles.menuItem} ${
                  active ? styles.menuItemActive : ""
                }`}
                style={{ "--option-fg": colors.fg || undefined }}
                onClick={() => onPick?.(opt.value)}
              >
                <span className={styles.optionDot} aria-hidden="true" />
                <span className={styles.menuItemLabel}>{opt.label}</span>
                {active && (
                  <span className={styles.menuCheck} aria-hidden="true">
                    <Check size={14} />
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>,
    document.body
  );
};

const StatusPill = ({
  value,
  label,
  options,
  onChange,
  colorMap,
  size = "sm",
  dot = true,
  enableSearch = true,
  className = "",
  ariaLabel,
  // Salt-görüntü için interactive=false geçilebilir; aksi halde options var ise
  // otomatik interactive olur.
  interactive,
}) => {
  const triggerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const id = useId();
  const normalizedOptions = useMemo(() => normalizeOptions(options), [options]);
  const isInteractive =
    interactive ?? (Array.isArray(options) && options.length > 0 && typeof onChange === "function");

  const palette = resolveColors({
    value,
    options: normalizedOptions,
    colorMap,
  });

  const close = useCallback(() => setOpen(false), []);
  const pick = useCallback(
    (next) => {
      if (next !== value) onChange?.(next);
      setOpen(false);
    },
    [onChange, value]
  );

  return (
    <span className={className}>
      <PillButton
        ref={triggerRef}
        value={value}
        label={label}
        palette={palette}
        size={size}
        interactive={isInteractive}
        dot={dot}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        ariaLabel={ariaLabel || `Durum: ${label || value}`}
        ariaExpanded={open}
      />
      {isInteractive && open && (
        <PillMenu
          triggerRef={triggerRef}
          options={normalizedOptions}
          value={value}
          onPick={pick}
          onClose={close}
          enableSearch={enableSearch && normalizedOptions.length > 6}
          colorMap={colorMap}
        />
      )}
    </span>
  );
};

export default StatusPill;
